/**
 * 综合状态栏组件
 * 整合显示版本号、用户信息、后台服务状态、连接状态、分数等信息
 */

import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store/store';
import { logout } from '../store/authSlice';
import backgroundHealthCheck from '../services/backgroundHealthCheck';
import { VERSION_INFO, APP_CONFIG } from '../config/version';
import { checkAPIHealth, API_BASE_URL } from '../config/api';

interface StatusData {
  isHealthCheckRunning: boolean;
  lastPing: Date | null;
  isApiHealthy: boolean;
  totalScore: number;
  todayScore: number;
}

interface StatusBarProps {
  onNavigateToProfile?: () => void;
}

const StatusBar: React.FC<StatusBarProps> = ({ onNavigateToProfile }) => {
  const dispatch = useDispatch();
  const [status, setStatus] = useState<StatusData>({
    isHealthCheckRunning: false,
    lastPing: null,
    isApiHealthy: false,
    totalScore: 0,
    todayScore: 0,
  });

  const [showDetails, setShowDetails] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  // 从Redux获取用户信息
  const { user, isGuestMode, guestUsage } = useSelector((state: RootState) => state.auth);

  // 处理退出登录
  const handleLogout = () => {
    dispatch(logout());
    setShowUserMenu(false);
  };

  // 刷新状态
  const refreshStatus = async () => {
    try {
      // 获取健康检查服务状态
      const healthCheckStatus = backgroundHealthCheck.getStatus();

      // 检查API连接状态
      const isApiHealthy = await checkAPIHealth();

      setStatus(prevStatus => ({
        ...prevStatus,
        isHealthCheckRunning: healthCheckStatus.isRunning,
        lastPing: healthCheckStatus.lastPing,
        isApiHealthy,
        // 从wallet状态获取实际分数
        totalScore: isGuestMode ? guestUsage.messageCount * 10 + guestUsage.practiceCount * 20 : 1250,
        todayScore: isGuestMode ? guestUsage.messageCount * 5 : 85
      }));
    } catch (error) {
      console.warn('Failed to refresh status:', error);
    }
  };

  // 定期更新状态
  useEffect(() => {
    refreshStatus();
    const interval = setInterval(refreshStatus, 10000); // 每10秒更新
    return () => clearInterval(interval);
  }, []);

  // 获取服务器域名（截断显示）
  const getServerDomain = () => {
    try {
      const url = new URL(API_BASE_URL);
      const hostname = url.hostname;
      // 限制显示长度为15个字符，超出部分用省略号代替
      if (hostname.length > 15) {
        return hostname.substring(0, 12) + '...';
      }
      return hostname;
    } catch {
      const fallback = API_BASE_URL;
      if (fallback.length > 15) {
        return fallback.substring(0, 12) + '...';
      }
      return fallback;
    }
  };

  // 获取连接状态指示器
  const getConnectionIndicator = () => {
    if (!APP_CONFIG.ENABLE_HEALTH_CHECK) {
      return { color: '#95a5a6', text: getServerDomain(), icon: '⚪' };
    }

    if (status.isApiHealthy) {
      return { color: '#27ae60', text: getServerDomain(), icon: '🟢' };
    } else {
      return { color: '#e74c3c', text: getServerDomain(), icon: '🔴' };
    }
  };

  // 获取后台服务状态
  const getServiceStatus = () => {
    if (!APP_CONFIG.ENABLE_HEALTH_CHECK) {
      return '服务未启用';
    }

    return status.isHealthCheckRunning ? '运行中' : '已停止';
  };

  const indicator = getConnectionIndicator();

  return (
    <View style={styles.container}>
      {/* 主状态栏 */}
      <View style={styles.statusRow}>
        {/* 左侧：用户信息和版本 */}
        <TouchableOpacity
          style={styles.leftSection}
          onPress={() => setShowUserMenu(!showUserMenu)}
          activeOpacity={0.7}
        >
          <View style={styles.userContainer}>
            <Text style={styles.appNameText}>ChatFluent</Text>
            <Text style={styles.versionText}>v{VERSION_INFO.version}</Text>
          </View>
        </TouchableOpacity>

        {/* 中间：连接状态 */}
        <TouchableOpacity
          style={styles.centerSection}
          onPress={() => setShowDetails(!showDetails)}
          activeOpacity={0.7}
        >
          <Text style={styles.connectionIcon}>{indicator.icon}</Text>
          <Text style={[styles.connectionText, { color: indicator.color }]}>
            {APP_CONFIG.BACKEND_TARGET === 'development' ? '开发' : '生产'}
          </Text>
        </TouchableOpacity>

        {/* 右侧：用户分数信息（可点击到个人中心） */}
        <TouchableOpacity
          style={styles.profileButton}
          onPress={onNavigateToProfile}
          activeOpacity={0.7}
        >
          <Text style={styles.userScoreText}>
            {isGuestMode ? `游客: ${status.totalScore}` : `${user?.display_name || 'User'}: ${status.totalScore}`}
          </Text>
          <Text style={styles.todayScoreText}>今日: {status.todayScore}</Text>
        </TouchableOpacity>
      </View>

      {/* 用户菜单 */}
      {showUserMenu && (
        <View style={styles.menuContainer}>
          <View style={styles.userInfo}>
            <Text style={styles.userInfoTitle}>
              {isGuestMode ? '游客模式' : '用户信息'}
            </Text>
            {isGuestMode ? (
              <Text style={styles.userInfoText}>
                剩余对话: {5 - guestUsage.messageCount} 次{'\n'}
                剩余练习: {5 - guestUsage.practiceCount} 次
              </Text>
            ) : (
              <Text style={styles.userInfoText}>
                {user?.email || '未知用户'}
              </Text>
            )}
          </View>

          {!isGuestMode && (
            <TouchableOpacity style={styles.logoutButton} onPress={handleLogout}>
              <Text style={styles.logoutButtonText}>退出登录</Text>
            </TouchableOpacity>
          )}
        </View>
      )}

      {/* 详细信息（可展开） */}
      {showDetails && (
        <View style={styles.detailsContainer}>
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>后台服务:</Text>
            <Text style={styles.detailValue}>{getServiceStatus()}</Text>
          </View>

          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>最后检查:</Text>
            <Text style={styles.detailValue}>
              {status.lastPing ? status.lastPing.toLocaleTimeString() : '从未'}
            </Text>
          </View>

          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>服务状态:</Text>
            <Text style={styles.detailValue}>{indicator.text}</Text>
          </View>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#f8f9fa',
    borderBottomWidth: 1,
    borderBottomColor: '#e9ecef',
  },
  statusRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  leftSection: {
    flex: 1,
    alignItems: 'flex-start',
  },
  userContainer: {
    alignItems: 'flex-start',
  },
  appNameText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
  },
  versionText: {
    fontSize: 10,
    color: '#6c757d',
    marginTop: 1,
  },
  centerSection: {
    alignItems: 'center',
    flexDirection: 'row',
    paddingHorizontal: 12,
  },
  connectionIcon: {
    fontSize: 12,
    marginRight: 4,
  },
  connectionText: {
    fontSize: 12,
    fontWeight: '500',
  },
  profileButton: {
    alignItems: 'flex-end',
    backgroundColor: '#ffffff',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e9ecef',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 1,
  },
  userScoreText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#495057',
  },
  todayScoreText: {
    fontSize: 10,
    color: '#6c757d',
  },
  menuContainer: {
    backgroundColor: '#ffffff',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: '#e9ecef',
    borderBottomWidth: 1,
    borderBottomColor: '#e9ecef',
  },
  userInfo: {
    marginBottom: 8,
  },
  userInfoTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: '#495057',
    marginBottom: 4,
  },
  userInfoText: {
    fontSize: 11,
    color: '#6c757d',
    lineHeight: 16,
  },
  logoutButton: {
    alignSelf: 'flex-start',
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: '#dc3545',
    borderRadius: 6,
  },
  logoutButtonText: {
    fontSize: 12,
    color: '#ffffff',
    fontWeight: '500',
  },
  detailsContainer: {
    backgroundColor: '#ffffff',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderTopWidth: 1,
    borderTopColor: '#e9ecef',
  },
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 2,
  },
  detailLabel: {
    fontSize: 11,
    color: '#6c757d',
    flex: 1,
  },
  detailValue: {
    fontSize: 11,
    color: '#495057',
    flex: 2,
    textAlign: 'right',
  },
});

export default StatusBar;
